import clsx from 'clsx';
import React, { memo, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';

import { PrimaryButton } from '@components/Button/variants';
import IconMail from '@components/Images/Rebranding/IconMail';
import Spinner from '@components/Spinner';
import NewsletterActionButton from '@components/pages/NewsletterIndexLanding/NewsletterCard/NewsletterActionButton';
import useCurrentNewsletterSubscription from '@hooks/useCurrentNewsletterSubscription';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import useNewsletter from '@hooks/useNewsletter';
import useSubscribeToNewsletter from '@hooks/useSubscribeToNewsletter';
import { newsletter_list_path } from '@routes';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';

import './styles.scss';

const VIEW_LIST_TEXT = 'View all newsletters';

interface SubscribeToNewsletterProps {
  id: string | number;
}

const SubscribeToNewsletter = ({ id: newsletterId }: SubscribeToNewsletterProps): JSX.Element => {
  const [successfullySubscribed, setSuccessfullySubscribed] = useState(false);
  const { isSubscribed: isSubscribedToNewsletter, isLoadingSubscribed } =
    useCurrentNewsletterSubscription(newsletterId, !successfullySubscribed);
  const { newsletter, isLoadingNewsletter } = useNewsletter(newsletterId);
  const { trackEvent } = useEventTracking({ location: TrackingLocation.SubscribeToNewsletter });
  const { mutate: subscribeToNewsletter } = useSubscribeToNewsletter();
  const { currentUser, isLoadingCurrentUser } = useCurrentUser();

  const handleSubscribe = () => {
    try {
      subscribeToNewsletter({ newsletter_id: newsletterId });
      setSuccessfullySubscribed(true);
    } catch (err) {
      setSuccessfullySubscribed(false);
    }
  };

  const extraThePrefix = useMemo(() => {
    const name = newsletter?.name ?? '';
    if (name.startsWith('The')) {
      return '';
    } else {
      return 'the ';
    }
  }, [newsletter?.name]);

  if (!newsletterId || isLoadingCurrentUser) return <></>;
  const canSubscribeToNewsletter =
    newsletter != null && currentUser != null && currentUser.access_level >= newsletter.accessLevel;

  return (
    <aside
      className={clsx(
        'relative my-32 min-h-[63px] bg-[linear-gradient(to_right,_#000_0%,_#2f3538_100%)] p-[14px]',
        {
          'pr-10': !isSubscribedToNewsletter,
        }
      )}
      data-testid="newsletter-article"
    >
      {!isLoadingSubscribed && !isLoadingNewsletter ? (
        <InView
          className="flex flex-row items-center justify-between gap-16"
          onChange={(inView) => {
            inView &&
              trackEvent(
                impression({
                  impressionElement: ImpressionElement.NewsletterArticleSignup,
                  contentId: newsletterId?.toString(),
                  context: `subscribed:${(isSubscribedToNewsletter ?? false).toString()}`,
                })
              );
          }}
          triggerOnce
        >
          <p
            className={clsx(
              '!m-0 flex flex-row items-center gap-[0.5ch] !font-suisse-intl font-normal !text-white',
              {
                'text-body-md': isSubscribedToNewsletter,
                'text-paragraph': !isSubscribedToNewsletter,
              }
            )}
          >
            <span className="pointer-events-none relative -mt-2 mr-8 hidden flex-shrink-0 items-center justify-center sm:flex">
              <IconMail className="fill-primary-magenta stroke-1" height={18} width={26} />
              <span className="newsletter-mail-count absolute -right-6 -top-8 flex h-16 w-16 items-center justify-center rounded-full bg-primary-magenta font-suisse-intl text-overline-sm font-bold text-primary-black">
                1
              </span>
            </span>
            <span>
              You’re reading {extraThePrefix}
              <span className="font-bold">{newsletter?.name}</span> Newsletter
            </span>
          </p>
          {isSubscribedToNewsletter ? (
            <PrimaryButton
              as="link"
              className="!no-underline max-sm:!w-fit dark:hover:!text-primary-magenta"
              href={newsletter_list_path()}
              onClickCapture={(evt) => {
                evt.stopPropagation();
                trackEvent(click({ clickTarget: ClickTarget.ViewList }));
              }}
              size="small"
            >
              {VIEW_LIST_TEXT}
            </PrimaryButton>
          ) : (
            <NewsletterActionButton
              canSubscribeToNewsletter={canSubscribeToNewsletter}
              currentUserSubscribed={isSubscribedToNewsletter}
              id={Number(newsletterId)}
              initialCurrentUser={currentUser}
              isCurrentlySubscriber={currentUser?.is_subscriber}
              onClickCapture={() =>
                trackEvent(
                  click({ clickTarget: ClickTarget.Subscribe, clickText: newsletter?.name })
                )
              }
              updateStateHandlerOnSubscribe={handleSubscribe}
              updateStateHandlerOnUnsubscribe={handleSubscribe}
            />
          )}
        </InView>
      ) : (
        <div className="*:!mb-0 *:!mt-4">
          <Spinner marginY="small" />
        </div>
      )}
    </aside>
  );
};

export default memo(SubscribeToNewsletter);
